import { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { getUserSettings, updateUserSettings } from '../data/settings';

export interface Settings {
  darkMode: boolean;
  showTutorial: boolean;
  betaFeatures: boolean;
  showStudyTimer: boolean;
}

interface SettingsContextType {
  settings: Settings;
  updateSettings: (newSettings: Partial<Settings>) => Promise<void>;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export function SettingsProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = useState<Settings>({
    darkMode: false,
    showTutorial: true,
    betaFeatures: false,
    showStudyTimer: true
  });
  const { user } = useAuth();

  useEffect(() => {
    const fetchSettings = async () => {
      if (!user) return;

      try {
        const userSettings = await getUserSettings(user.uid);
        if (userSettings) {
          // Extract only the settings we need for the UI
          const uiSettings: Settings = {
            darkMode: userSettings.darkMode,
            showTutorial: userSettings.showTutorial,
            betaFeatures: userSettings.betaFeatures,
            showStudyTimer: userSettings.showStudyTimer ?? true
          };
          setSettings(uiSettings);
        } else {
          // Initialize default settings
          const defaultSettings = {
            darkMode: false,
            showTutorial: true,
            betaFeatures: false,
            showStudyTimer: true
          };
          await updateUserSettings(user.uid, defaultSettings);
          setSettings(defaultSettings);
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };

    fetchSettings();
  }, [user]);

  const updateSettings = async (newSettings: Partial<Settings>) => {
    if (!user) return;

    try {
      const updatedSettings = { ...settings, ...newSettings };
      await updateUserSettings(user.uid, updatedSettings);
      setSettings(updatedSettings);
    } catch (error) {
      console.error('Error updating settings:', error);
    }
  };

  return (
    <SettingsContext.Provider value={{ settings, updateSettings }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
} 